import { useState } from 'react';
import type {
  HumanAxisState,
  HumanEvidenceReport,
  HumanStatement,
} from '@upl/human-report';
import { Badge } from '@upl/ui';
import {
  ArrowDownToLine,
  Check,
  CircleAlert,
  CircleMinus,
  Clipboard,
  Clock3,
  FileCode2,
  FileImage,
  Fingerprint,
  GitBranch,
  KeyRound,
  Layers3,
  LockKeyhole,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Waypoints,
  Workflow,
} from 'lucide-react';
import { Dropzone } from './Dropzone.js';
import type { ProviderCheckState } from './ProviderConsent.js';

interface ResultReportProps {
  report: HumanEvidenceReport;
  localReport: HumanEvidenceReport;
  providerState: ProviderCheckState;
  providerControl: React.ReactNode;
  previewUrl?: string | undefined;
  onReset: () => void;
  onFile: (file: File) => void;
}

export function ResultReport({ report, localReport, providerState, providerControl, previewUrl, onReset, onFile }: ResultReportProps) {
  const [copied, setCopied] = useState(false);

  const downloadReport = (): void => {
    const blob = new Blob([JSON.stringify(report.rawEvidence, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `provenance-evidence-${report.verification.id}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const copySummary = async (): Promise<void> => {
    await navigator.clipboard.writeText(report.copySummary);
    setCopied(true);
    globalThis.setTimeout(() => setCopied(false), 1600);
  };

  return (
    <article className="report" data-testid="human-evidence-report">
      <div className="report__toolbar">
        <button className="text-button" type="button" onClick={onReset}>
          <RefreshCw size={15} /> New verification
        </button>
        <div>
          <button className="icon-action" type="button" onClick={() => void copySummary()}>
            {copied ? <Check size={16} /> : <Clipboard size={16} />}
            {copied ? 'Summary copied' : 'Copy summary'}
          </button>
          <button className="icon-action" type="button" onClick={downloadReport}>
            <ArrowDownToLine size={16} /> Export normalized JSON
          </button>
        </div>
        <span className="sr-only" aria-live="polite">{copied ? 'Human-readable summary copied.' : ''}</span>
      </div>

      <section className={`result-hero result-hero--${report.tone}`} aria-labelledby="result-heading">
        <div className="result-hero__preview">
          {previewUrl ? <img src={previewUrl} alt="Locally inspected file preview" /> : <FileImage size={40} />}
          <span>{report.artifact.format} · {formatBytes(report.artifact.byte_size)}</span>
        </div>
        <div className="result-hero__content">
          <Badge tone={report.tone}>{report.eyebrow}</Badge>
          <h1 id="result-heading">{report.headline}</h1>
          <p>{report.summary}</p>
          {providerState.kind !== 'complete' && (
            <p className="result-hero__not-checked"><CircleMinus size={15} /> Additional provider-specific provenance signals were not checked.</p>
          )}
          <p className="result-hero__boundary"><CircleAlert size={15} />Provenance evidence does not establish factual truth.</p>
          <div className="result-hero__assurance">
            <LockKeyhole size={16} />
            <span><strong>{executionHeadline(report)}</strong>{executionSummary(report)}</span>
          </div>
        </div>
        <div className="result-hero__mark" aria-hidden="true">
          {report.tone === 'positive' ? <ShieldCheck /> : report.tone === 'warning' ? <CircleAlert /> : <Fingerprint />}
        </div>
      </section>

      <section className="verification-stages" aria-label="Verification stages">
        <StageCard number="01" title="Local inspection" state="COMPLETE" text={`${localReport.technicalEvidence.signals.length} normalized signal${localReport.technicalEvidence.signals.length === 1 ? '' : 's'} · source media stayed on this device.`} />
        <StageCard number="02" title="ORIGNTD resolution" state="NOT CHECKED" text="Detached/repository resolution was not requested by this verification." />
        <StageCard number="03" title="Provider signals" state={providerStageState(providerState)} text={providerStageText(providerState)} />
        <StageCard number="04" title="Synthesis" state="COMPLETE" text="Evidence sources remain separate and are combined without a probability score." />
      </section>

      {providerControl}

      <section className="execution-disclosure" aria-labelledby="execution-disclosure-heading">
        <div><span className="eyebrow">Actual execution</span><h2 id="execution-disclosure-heading">Where this verification ran</h2></div>
        <div className="execution-disclosure__grid">
          <Fact label="On this device" value={report.verification.execution.localProcessing ? 'Performed' : 'Not performed'} />
          <Fact label="ORIGNTD server" value={report.verification.execution.origntdServerProcessing ? 'Performed' : 'Not used'} />
          <Fact label="External provider" value={report.verification.execution.externalProviderProcessing ? 'Performed' : 'Not checked'} />
          <Fact label="Media transmitted" value={report.verification.execution.mediaTransmitted ? 'Yes' : 'No'} />
        </div>
        {report.verification.execution.destinations.length > 0 && (
          <ul className="execution-destinations">
            {report.verification.execution.destinations.map((destination) => (
              <li key={`${destination.kind}:${destination.name}`}><strong>{destination.name}</strong><span>{humanize(destination.executionState)} · media {destination.mediaTransmitted ? 'transmitted' : 'not transmitted'}</span></li>
            ))}
          </ul>
        )}
        <div className="retention-disclosure">
          <div><strong>ORIGNTD retention</strong><span>{humanize(report.verification.execution.origntdSourceMediaRetention)}</span></div>
          <div><strong>External-provider retention</strong><span>{humanize(report.verification.execution.externalProviderMediaRetention)}</span></div>
        </div>
      </section>

      <section className="axis-summary" aria-label="Five-axis evidence summary">
        {report.axes.map((axis) => (
          <article key={axis.id}>
            <span className="axis-summary__label">{axis.label}</span>
            <strong><StateGlyph state={axis.state} />{humanize(axis.state)}</strong>
            <small>{axis.explanation}</small>
          </article>
        ))}
      </section>

      <section className="evidence-overview" aria-label="Human evidence overview">
        <StatementColumn icon={<ShieldCheck size={17} />} title="Proven" statements={report.provenFacts} empty="No additional condition was proven." />
        <StatementColumn icon={<Waypoints size={17} />} title="Known from evidence" statements={report.knownFacts} empty="No additional declaration was available." />
        <StatementColumn icon={<CircleMinus size={17} />} title="Unknown or limited" statements={[...report.unknowns, ...report.limitations.slice(0, 2)]} empty="No contextual limit was generated." />
      </section>

      <section className="report-meta" aria-label="Verification metadata">
        <div><Fingerprint size={17} /><span>Verification ID</span><code>{shortId(report.verification.id)}</code></div>
        <div><Clock3 size={17} /><span>Processed in</span><strong>{formatDuration(report.verification.durationMs)}</strong></div>
        <div><FileCode2 size={17} /><span>Evidence schema</span><strong>v{report.verification.schemaVersion}</strong></div>
        <div><Layers3 size={17} /><span>History entries</span><strong>{report.history.count}</strong></div>
      </section>

      <div className="report-sections" data-testid="evidence-explorer">
        <ReportSection icon={<Workflow />} title="Provenance" summary="Credential presence and normalized evidence" defaultOpen>
          <div className="fact-grid">
            <Fact label="Credential" value={report.credentialSummary.present ? 'Present' : 'Not found'} />
            <Fact label="Credentials found" value={String(report.credentialSummary.count)} />
            <Fact label="Cryptographic validity" value={humanize(report.credentialSummary.cryptographicValidity)} />
            <Fact label="Asset binding" value={humanize(report.credentialSummary.assetBindingValidity)} />
          </div>
          <StatementList statements={[...report.provenFacts, ...report.knownFacts]} />
        </ReportSection>

        <ReportSection icon={<Sparkles />} title="Declared source" summary={`${report.source.length} digital source declaration${report.source.length === 1 ? '' : 's'}`}>
          {report.source.length > 0 ? (
            <div className="source-grid">
              {report.source.map((source) => (
                <article key={source.id}>
                  <span className="evidence-tag">Declared by credential</span>
                  <h3>{source.label}</h3>
                  <code>{source.sourceType}</code>
                  <p>{source.verificationAssessment === 'CREDENTIAL_VALIDATED'
                    ? 'ORIGNTD validated the credential carrying this declaration; it did not independently verify the declared source as a real-world fact.'
                    : 'ORIGNTD displays this credential declaration; it did not independently verify the declared source.'}</p>
                </article>
              ))}
            </div>
          ) : <EmptyEvidence>No supported digital source type was declared.</EmptyEvidence>}
        </ReportSection>

        <ReportSection icon={<Sparkles />} title="Creation / edit history" summary={`${report.history.count} action${report.history.count === 1 ? '' : 's'} · ${humanize(report.history.ordering)} order`}>
          <p className="ordering-note"><Clock3 size={14} />{report.history.ordering === 'CHRONOLOGICAL'
            ? 'All displayed actions include timestamps and are ordered chronologically.'
            : 'One or more timestamps are missing. The original logical declaration order is preserved.'}</p>
          {report.history.events.length > 0 ? (
            <ol className="timeline">
              {report.history.events.map((event) => (
                <li key={event.id}>
                  <span>{String(event.sequence).padStart(2, '0')}</span>
                  <div>
                    <strong>{event.label}</strong>
                    <small>{event.occurredAt ? formatDate(event.occurredAt) : 'Time not provided'}</small>
                    {(event.tool ?? event.actor ?? event.sourceType) && <em>{[event.tool, event.actor, event.sourceType].filter(Boolean).join(' · ')}</em>}
                  </div>
                </li>
              ))}
            </ol>
          ) : <EmptyEvidence>No supported creation or edit actions were declared.</EmptyEvidence>}
        </ReportSection>

        <ReportSection icon={<GitBranch />} title="Ingredient explorer" summary={`${report.ingredients.length} lineage branch${report.ingredients.length === 1 ? '' : 'es'}`}>
          <p className="section-note">Each ingredient keeps its own provenance and integrity state. Parent and ingredient validation are never collapsed into one result.</p>
          {report.ingredients.length > 0 ? (
            <div className="ingredient-grid">
              {report.ingredients.map((ingredient) => (
                <article key={ingredient.id}>
                  <span className="evidence-tag">Branch {shortId(ingredient.branchId)}</span>
                  <h3>{ingredient.title}</h3>
                  <dl>
                    <div><dt>Relationship</dt><dd>{humanize(ingredient.relationship)}</dd></div>
                    <div><dt>Ingredient provenance</dt><dd>{humanize(ingredient.provenance)}</dd></div>
                    <div><dt>Ingredient integrity</dt><dd>{humanize(ingredient.integrity)}</dd></div>
                    {ingredient.mediaType && <div><dt>Media type</dt><dd>{ingredient.mediaType}</dd></div>}
                  </dl>
                </article>
              ))}
            </div>
          ) : <EmptyEvidence>No ingredient lineage was declared.</EmptyEvidence>}
        </ReportSection>

        <ReportSection icon={<KeyRound />} title="Integrity, trust, and signer" summary="Independent dimensions, never one combined score">
          <div className="assurance-list">
            <AssuranceRow label="Credential signature" value={report.integrity.cryptographicValidity} />
            <AssuranceRow label="Binding to this file" value={report.integrity.assetBindingValidity} />
            <AssuranceRow label="Trust-policy result" value={report.trust.state} />
            <AssuranceRow label="Signer trust" value={report.trust.signerTrust} />
            <AssuranceRow label="Timestamp trust" value={report.trust.timestampTrust} />
            <AssuranceRow label="Trust data" value={report.trust.dataFreshness} />
            <AssuranceRow label="Signer identity" value={report.signer.identityStatus} />
          </div>
          {report.signer.available ? (
            <div className="signer-card">
              <span className="evidence-tag">Credential signer</span>
              <h3>{report.signer.displayName}</h3>
              <dl>
                {report.signer.certificateSubject && <div><dt>Certificate subject</dt><dd>{report.signer.certificateSubject}</dd></div>}
                {report.signer.issuer && <div><dt>Issuer</dt><dd>{report.signer.issuer}</dd></div>}
                {report.signer.signingTime && <div><dt>Signing time</dt><dd>{formatDate(report.signer.signingTime)}</dd></div>}
                {report.signer.algorithm && <div><dt>Algorithm</dt><dd>{report.signer.algorithm}</dd></div>}
                <div><dt>Identity status</dt><dd>{humanize(report.signer.identityStatus)}</dd></div>
              </dl>
            </div>
          ) : <EmptyEvidence>No signer identity was provided in supported evidence.</EmptyEvidence>}
          <div className="status-token-list">{report.trust.statuses.map((status) => <span key={status}>{humanize(status)}</span>)}</div>
          <p className="section-note">Policy: {report.trust.policyName} · {report.trust.policyVersion}{report.trust.policyId ? ` · ${report.trust.policyId}` : ''}</p>
          {report.trust.snapshots.length > 0 && (
            <div className="trust-snapshot-list">
              {report.trust.snapshots.map((snapshot) => (
                <article key={`${snapshot.policy_id}:${snapshot.content_digest}`}>
                  <span className="evidence-tag">{humanize(snapshot.status)} trust data</span>
                  <h3>{snapshot.policy_id}</h3>
                  <dl>
                    <div><dt>Snapshot</dt><dd>{snapshot.policy_version}</dd></div>
                    <div><dt>Retrieved</dt><dd>{formatDate(snapshot.retrieved_at)}</dd></div>
                    <div><dt>Anchors</dt><dd>{snapshot.anchor_count}</dd></div>
                    <div><dt>Digest</dt><dd><code>{shortId(snapshot.content_digest)}</code></dd></div>
                  </dl>
                </article>
              ))}
            </div>
          )}
          <p className="section-note">This trust assessment concerns the credential and signing chain. It does not establish whether the depicted event is factually true.</p>
        </ReportSection>

        <ReportSection icon={<Layers3 />} title="Capability disclosure" summary="What this runtime checks and does not check">
          <div className="capability-table">
            {report.capabilities.map((capability) => (
              <div key={capability.id}>
                <StateGlyph state={capabilityState(capability.state)} />
                <span><strong>{capability.label}</strong><small>{capability.description}</small></span>
                <b>{humanize(capability.state)}</b>
              </div>
            ))}
          </div>
        </ReportSection>

        <ReportSection icon={<Sparkles />} title="OpenAI provider evidence" summary={openAiSignals(report).length > 0 ? `${openAiSignals(report).length} independently reported signal${openAiSignals(report).length === 1 ? '' : 's'}` : 'Not checked or no provider evidence available'}>
          {openAiSignals(report).length > 0 ? (
            <div className="provider-evidence-grid">
              {openAiSignals(report).map((signal) => (
                <article key={signal.id}>
                  <span className="evidence-tag">OpenAI Content Provenance API</span>
                  <h3>{signal.kind === 'PROVIDER_C2PA_DETECTION' ? 'C2PA' : 'SynthID'}</h3>
                  <strong>{signal.state === 'PRESENT' ? 'Signal detected' : 'No supported signal detected'}</strong>
                  <dl>
                    <div><dt>Observation</dt><dd>{humanize(typeof signal.details.outcome === 'string' ? signal.details.outcome : signal.state)}</dd></div>
                    {typeof signal.details.validation_state === 'string' && <div><dt>Validation</dt><dd>{humanize(signal.details.validation_state)}</dd></div>}
                    {typeof signal.details.issuer === 'string' && <div><dt>Issuer</dt><dd>{signal.details.issuer}</dd></div>}
                    {typeof signal.details.model === 'string' && <div><dt>Model</dt><dd>{signal.details.model}</dd></div>}
                    {typeof signal.details.generated_at === 'string' && <div><dt>Generated</dt><dd>{formatDate(signal.details.generated_at)}</dd></div>}
                  </dl>
                  <p>{signal.state === 'PRESENT'
                    ? 'OpenAI detected supported provenance evidence associated with its generation systems.'
                    : 'This does not establish human authorship and does not rule out AI generation.'}</p>
                </article>
              ))}
            </div>
          ) : <EmptyEvidence>OpenAI provider signals were not checked in this result.</EmptyEvidence>}
        </ReportSection>

        <ReportSection icon={<CircleAlert />} title="Warnings and limitations" summary={`${report.warnings.length} warning${report.warnings.length === 1 ? '' : 's'} · ${report.limitations.length} limitation${report.limitations.length === 1 ? '' : 's'}`}>
          <LimitationGroups warnings={report.warnings} unknowns={report.unknowns} limitations={report.limitations} />
        </ReportSection>

        <ReportSection icon={<Fingerprint />} title="Technical evidence" summary={`${report.technicalEvidence.signals.length} signals · ${report.technicalEvidence.assertions.length} assertions`}>
          <div className="signal-list">
            {report.technicalEvidence.signals.map((signal) => (
              <div key={signal.id}>
                <span className={`signal-dot signal-dot--${signal.state.toLowerCase()}`} />
                <div><strong>{signal.label}</strong><small>{[signal.kind, signal.state, signal.evidence_method, signal.evidence_strength].filter(Boolean).join(' · ')}</small></div>
                <code>{shortId(signal.id)}</code>
              </div>
            ))}
          </div>
          <div className="technical-counts">
            <Fact label="Claims" value={String(report.technicalEvidence.claims.length)} />
            <Fact label="Assertions" value={String(report.technicalEvidence.assertions.length)} />
            <Fact label="Graph edges" value={String(report.technicalEvidence.edges.length)} />
            <Fact label="Tools" value={String(report.technicalEvidence.tools.length)} />
          </div>
        </ReportSection>

        <ReportSection icon={<FileCode2 />} title="Raw evidence" summary="Canonical normalized evidence, with provider output kept separate">
          <h3 className="raw-heading">Canonical normalized evidence</h3>
          <p className="section-note">This is the provider-independent JSON exported by the report action above.</p>
          <pre className="raw-data" tabIndex={0}>{JSON.stringify(report.rawEvidence, null, 2)}</pre>
          {report.providerRawEvidence && (
            <details className="provider-raw">
              <summary>Sanitized provider raw evidence</summary>
              <p className="section-note">Provider-specific output is labeled separately and is not the canonical report contract.</p>
              <pre className="raw-data" tabIndex={0}>{JSON.stringify(report.providerRawEvidence, null, 2)}</pre>
            </details>
          )}
        </ReportSection>
      </div>

      <section className="verify-another">
        <div><span className="eyebrow">Continue investigating</span><h2>Check another image.</h2></div>
        <Dropzone compact onFile={onFile} />
      </section>
    </article>
  );
}

function StageCard({ number, title, state, text }: { number: string; title: string; state: string; text: string }) {
  return <article><small>{number}</small><span>{state}</span><h2>{title}</h2><p>{text}</p></article>;
}

function providerStageState(state: ProviderCheckState): string {
  if (state.kind === 'complete') return 'COMPLETE';
  if (state.kind === 'running') return 'RUNNING';
  if (state.kind === 'error') return 'FAILED';
  if (state.kind === 'not_configured') return 'NOT CONFIGURED';
  return 'NOT CHECKED';
}

function providerStageText(state: ProviderCheckState): string {
  if (state.kind === 'complete') return 'OpenAI C2PA and SynthID observations were checked after explicit consent.';
  if (state.kind === 'running') return 'The explicitly approved OpenAI check is in progress.';
  if (state.kind === 'error') return 'The provider boundary failed safely; the local evidence remains available.';
  if (state.kind === 'not_configured') return 'The deployment has no configured OpenAI provider credential. No media was sent.';
  return 'OpenAI provenance signals were not checked.';
}

function executionHeadline(report: HumanEvidenceReport): string {
  return report.verification.execution.externalProviderProcessing
    ? 'Local and external checks completed.'
    : report.verification.execution.origntdServerProcessing ? 'Processed by ORIGNTD.' : 'Processed on this device.';
}

function executionSummary(report: HumanEvidenceReport): string {
  if (!report.verification.execution.mediaTransmitted) return ' No image upload or storage was used.';
  return ` Source-media retention by ORIGNTD: ${humanize(report.verification.execution.origntdSourceMediaRetention).toLowerCase()}.`;
}

function openAiSignals(report: HumanEvidenceReport) {
  return report.technicalEvidence.signals.filter(({ kind }) => kind === 'PROVIDER_C2PA_DETECTION' || kind === 'PROVIDER_SYNTHID_DETECTION');
}

function StatementColumn({ icon, title, statements, empty }: { icon: React.ReactNode; title: string; statements: readonly HumanStatement[]; empty: string }) {
  return <div><h2>{icon}{title}</h2><ul>{(statements.length ? statements : [{ id: 'empty', text: empty }]).slice(0, 4).map((statement) => <li key={statement.id}>{statement.text}</li>)}</ul></div>;
}

function StatementList({ statements }: { statements: readonly HumanStatement[] }) {
  if (statements.length === 0) return <EmptyEvidence>No additional statement was generated.</EmptyEvidence>;
  return (
    <div className="statement-list">
      {statements.map((statement) => (
        <article key={statement.id}>
          <span className={`statement-kind statement-kind--${statement.kind.toLowerCase()}`}>{statement.kind}</span>
          <p>{statement.text}</p>
          <code>{statement.traceRefs.map(shortId).join(' · ')}</code>
        </article>
      ))}
    </div>
  );
}

function LimitationGroups({ warnings, unknowns, limitations }: {
  warnings: readonly HumanStatement[];
  unknowns: readonly HumanStatement[];
  limitations: readonly HumanStatement[];
}) {
  const groups = [
    { title: 'Evidence boundaries', values: [...unknowns, ...limitations.filter(({ id }) => /truth|author|absence|metadata/i.test(id))] },
    { title: 'Checks not performed', values: limitations.filter(({ id }) => /capability|not-checked|unsupported/i.test(id)) },
    { title: 'Runtime / provider limitations', values: [...warnings, ...limitations.filter(({ id }) => /adapter|provider|parser|trust-source|timeout/i.test(id))] },
    { title: 'Privacy / execution', values: limitations.filter(({ id }) => /privacy|processing|transmission|retention/i.test(id)) },
  ].map((group) => ({ ...group, values: [...new Map(group.values.map((value) => [value.id, value])).values()] }))
    .filter(({ values }) => values.length > 0);
  const groupedIds = new Set(groups.flatMap(({ values }) => values.map(({ id }) => id)));
  const other = limitations.filter(({ id }) => !groupedIds.has(id));
  if (other.length > 0) groups.push({ title: 'Other supported limitations', values: other });
  return (
    <div className="limitation-groups">
      {groups.map((group, index) => (
        <details key={group.title} open={index === 0}>
          <summary><strong>{group.title}</strong><span>{group.values.length}</span></summary>
          <StatementList statements={group.values} />
        </details>
      ))}
    </div>
  );
}

function ReportSection({ icon, title, summary, defaultOpen, children }: React.PropsWithChildren<{ icon: React.ReactNode; title: string; summary: string; defaultOpen?: boolean }>) {
  return (
    <details className="report-section" open={defaultOpen}>
      <summary><span className="report-section__icon">{icon}</span><span><strong>{title}</strong><small>{summary}</small></span><span className="report-section__toggle">+</span></summary>
      <div className="report-section__body">{children}</div>
    </details>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return <div><span>{label}</span><strong>{value}</strong></div>;
}

function AssuranceRow({ label, value }: { label: string; value: string }) {
  return <div><span>{label}</span><strong>{humanize(value)}</strong><StateGlyph state={assuranceState(value)} /></div>;
}

function StateGlyph({ state }: { state: HumanAxisState }) {
  return <i className={`state-glyph state-glyph--${state.toLowerCase()}`} aria-hidden="true" />;
}

function EmptyEvidence({ children }: React.PropsWithChildren) {
  return <p className="empty-evidence"><CircleMinus size={17} />{children}</p>;
}

function capabilityState(state: HumanEvidenceReport['capabilities'][number]['state']): HumanAxisState {
  if (state === 'SUPPORTED') return 'VERIFIED';
  if (state === 'NOT_SUPPORTED') return 'ABSENT';
  return 'UNRESOLVED';
}

function assuranceState(value: string): HumanAxisState {
  if (value === 'VALID' || value === 'KNOWN') return 'VERIFIED';
  if (value === 'INVALID') return 'INVALID';
  if (value === 'NOT_APPLICABLE' || value === 'NOT_PROVIDED') return 'NOT_RECORDED';
  return 'UNRESOLVED';
}

function humanize(value: string): string {
  return value.replace(/^c2pa\./, '').replaceAll('_', ' ').replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase().replace(/^./, (letter) => letter.toUpperCase());
}

function formatBytes(bytes: number): string {
  return bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function formatDuration(milliseconds: number): string {
  return milliseconds < 1000 ? `${Math.round(milliseconds)} ms` : `${(milliseconds / 1000).toFixed(1)} s`;
}

function shortId(value: string): string {
  return value.length > 20 ? `${value.slice(0, 10)}…${value.slice(-6)}` : value;
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat('en', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value));
}
